import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useBrainStore } from '@/state/brainStore';
import { useUIStore } from '@/state/uiStore';
import { useSimulationStore } from '@/state/simulationStore';
import type { CustomerRecord, ConversationEntry, FutureReadinessSignal } from '@/domain/types';
import { customerName, simTimeFull } from '@/utils/format';
import { X, Send, ScrollText, Calendar as CalendarIcon, Sparkles, XCircle } from 'lucide-react';

export type CustomerAction = 'sms' | 'meta' | 'rapport' | 'future' | 'handoff' | 'cancel' | null;

interface Props {
  customer: CustomerRecord;
  open: CustomerAction;
  onClose: () => void;
}

export default function CustomerActionModals({ customer, open, onClose }: Props) {
  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          key={open}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-overlay/80 backdrop-blur-sm flex items-center justify-center px-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 8, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="bg-ink-deep border border-gold-dim/40 rounded-xl w-full max-w-lg p-6 relative"
            onClick={(e) => e.stopPropagation()}
            data-testid={`modal-${open}`}
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute top-3 right-3 text-cream-soft hover:text-cream"
              aria-label="Close"
            >
              <X size={16} />
            </button>
            {open === 'sms' ? <SmsModal customer={customer} channel="sms" onDone={onClose} /> : null}
            {open === 'meta' ? <SmsModal customer={customer} channel="meta-dm" onDone={onClose} /> : null}
            {open === 'rapport' ? <RapportModal customer={customer} onDone={onClose} /> : null}
            {open === 'future' ? <FutureModal customer={customer} onDone={onClose} /> : null}
            {open === 'handoff' ? <HandoffModal customer={customer} onDone={onClose} /> : null}
            {open === 'cancel' ? <CancelModal customer={customer} onDone={onClose} /> : null}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

const SMS_TEMPLATES = [
  { label: 'Deposit nudge', body: 'Hey {first}, just following up on the deposit link for your session. Want me to extend the expiry?' },
  { label: 'Reschedule offer', body: 'Hi {first}, looking at the calendar I can move your session. Want me to check artist availability?' },
  { label: 'Pre-session prep', body: 'Hey {first}, quick reminder for your session: eat beforehand, stay hydrated, and bring photo ID.' },
  { label: 'Custom', body: '' },
];

function SmsModal({
  customer,
  channel,
  onDone,
}: {
  customer: CustomerRecord;
  channel: 'sms' | 'meta-dm';
  onDone: () => void;
}) {
  const addConvo = useBrainStore((s) => s.addConversationEntry);
  const appendActivity = useBrainStore((s) => s.appendActivity);
  const pushNotification = useUIStore((s) => s.pushNotification);
  const clock = useSimulationStore((s) => s.clockIso);
  const [template, setTemplate] = useState(SMS_TEMPLATES[0]!);
  const [body, setBody] = useState(template.body.replace('{first}', customer.firstName));

  const onSend = () => {
    const entry: ConversationEntry = {
      id: `msg-${Date.now()}`,
      customerId: customer.id,
      channel,
      direction: 'out',
      body,
      timestamp: clock,
      authorRole: 'agent',
      authorName: 'You',
    };
    addConvo(customer.id, entry);
    appendActivity({
      id: `act-${entry.id}`,
      type: channel === 'sms' ? 'sms-sent' : 'meta-dm',
      customerId: customer.id,
      body: `${channel === 'sms' ? 'SMS' : 'Meta DM'} sent to ${customerName(customer)}.`,
      timestamp: clock,
    });
    pushNotification({
      intensity: 'info',
      title: 'Message sent',
      body: `Delivered to ${customerName(customer)}.`,
      ttlMs: 4000,
    });
    onDone();
  };

  return (
    <>
      <p className="label-small-caps text-gold-bright">
        <Send size={12} className="inline mr-2" />
        {channel === 'sms' ? 'Send SMS' : 'Reply on Meta DM'}
      </p>
      <h2 className="heading-display text-2xl text-cream mt-2">{customerName(customer)}</h2>
      <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
        {channel === 'sms' ? customer.phone : customer.instagramHandle ?? customer.phone}
      </p>

      <label className="block mt-5">
        <span className="label-small-caps">Template</span>
        <select
          value={template.label}
          onChange={(e) => {
            const t = SMS_TEMPLATES.find((x) => x.label === e.target.value) ?? SMS_TEMPLATES[0]!;
            setTemplate(t);
            setBody(t.body.replace('{first}', customer.firstName));
          }}
          className="mt-2 w-full bg-ink-black border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
          data-testid="sms-template"
        >
          {SMS_TEMPLATES.map((t) => (
            <option key={t.label}>{t.label}</option>
          ))}
        </select>
      </label>

      <label className="block mt-3">
        <span className="label-small-caps">Message</span>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
          className="mt-2 w-full bg-ink-black border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream font-serif"
          data-testid="sms-body"
        />
      </label>

      <div className="flex gap-2 mt-5 justify-end">
        <button type="button" onClick={onDone} className="btn-ghost text-xs">
          Cancel
        </button>
        <button
          type="button"
          onClick={onSend}
          className="btn-primary text-xs"
          disabled={!body.trim()}
          data-testid="send-sms-confirm"
        >
          <Send size={12} /> Send
        </button>
      </div>
    </>
  );
}

function RapportModal({ customer, onDone }: { customer: CustomerRecord; onDone: () => void }) {
  const upsert = useBrainStore((s) => s.upsertCustomer);
  const appendActivity = useBrainStore((s) => s.appendActivity);
  const pushNotification = useUIStore((s) => s.pushNotification);
  const clock = useSimulationStore((s) => s.clockIso);
  const [note, setNote] = useState('');

  const onSave = () => {
    const updated: CustomerRecord = {
      ...customer,
      rapportNotes: [
        {
          id: `rapport-${Date.now()}`,
          note,
          author: 'You',
          timestamp: clock,
        },
        ...customer.rapportNotes,
      ],
    };
    upsert(updated);
    appendActivity({
      id: `act-rap-${Date.now()}`,
      type: 'meta-dm',
      customerId: customer.id,
      body: `Rapport note added to ${customerName(customer)}.`,
      timestamp: clock,
    });
    pushNotification({
      intensity: 'info',
      title: 'Rapport note saved',
      body: `Pinned to ${customerName(customer)}.`,
      ttlMs: 4000,
    });
    onDone();
  };

  return (
    <>
      <p className="label-small-caps text-gold-bright">
        <ScrollText size={12} className="inline mr-2" /> Add rapport note
      </p>
      <h2 className="heading-display text-2xl text-cream mt-2">{customerName(customer)}</h2>
      <p className="font-serif text-cream-soft text-sm mt-2">
        Quick context the team should remember. Pet&apos;s name, hometown, life event, anything that
        warms the next interaction.
      </p>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={5}
        className="mt-4 w-full bg-ink-black border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream font-serif"
        placeholder="e.g. Mum just had surgery, send a card before the session"
        data-testid="rapport-note-body"
      />
      <div className="flex gap-2 mt-5 justify-end">
        <button type="button" onClick={onDone} className="btn-ghost text-xs">
          Cancel
        </button>
        <button
          type="button"
          onClick={onSave}
          className="btn-primary text-xs"
          disabled={!note.trim()}
          data-testid="rapport-save"
        >
          Save note
        </button>
      </div>
    </>
  );
}

function FutureModal({ customer, onDone }: { customer: CustomerRecord; onDone: () => void }) {
  const upsert = useBrainStore((s) => s.upsertCustomer);
  const upsertSignal = useBrainStore((s) => s.upsertFutureSignal);
  const appendActivity = useBrainStore((s) => s.appendActivity);
  const pushNotification = useUIStore((s) => s.pushNotification);
  const clock = useSimulationStore((s) => s.clockIso);
  const [phrase, setPhrase] = useState('');
  const defaultMonths = '3';
  const [months, setMonths] = useState(defaultMonths);

  const onSave = () => {
    const fireAt = new Date(clock);
    fireAt.setMonth(fireAt.getMonth() + parseInt(months, 10));
    const signal: FutureReadinessSignal = {
      id: `sig-${Date.now()}`,
      customerId: customer.id,
      rawPhrase: phrase,
      scheduledFor: fireAt.toISOString(),
      recommendedChannel: 'sms',
      status: 'scheduled',
      createdAt: clock,
      draftMessage: `Hey ${customer.firstName}, you mentioned ${phrase.toLowerCase()}. Want to lock in a date?`,
    };
    upsertSignal(signal);
    upsert({
      ...customer,
      futureReadinessSignals: [signal, ...customer.futureReadinessSignals],
    });
    appendActivity({
      id: `act-fr-${signal.id}`,
      type: 'future-readiness-captured',
      customerId: customer.id,
      body: `Future-readiness captured for ${customerName(customer)}: "${phrase}".`,
      timestamp: clock,
    });
    pushNotification({
      intensity: 'info',
      title: 'Future-readiness scheduled',
      body: `${customerName(customer)} re-contact set ${months} months out.`,
      ttlMs: 4000,
    });
    onDone();
  };

  return (
    <>
      <p className="label-small-caps text-gold-bright">
        <Sparkles size={12} className="inline mr-2" /> Tag future-readiness
      </p>
      <h2 className="heading-display text-2xl text-cream mt-2">{customerName(customer)}</h2>
      <p className="font-serif text-cream-soft text-sm mt-2">
        Capture the phrase, set the re-contact window. The AI drafts the outreach when it fires.
      </p>
      <label className="block mt-4">
        <span className="label-small-caps">Phrase the customer used</span>
        <input
          type="text"
          value={phrase}
          onChange={(e) => setPhrase(e.target.value)}
          placeholder="e.g. after my bonus, mid-May"
          className="mt-2 w-full bg-ink-black border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
          data-testid="future-phrase"
        />
      </label>
      <label className="block mt-3">
        <span className="label-small-caps">Re-contact in (months)</span>
        <select
          value={months}
          onChange={(e) => setMonths(e.target.value)}
          className="mt-2 w-full bg-ink-black border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
        >
          {[1, 2, 3, 4, 6, 9, 12].map((m) => (
            <option key={m} value={m}>
              {m} {m === 1 ? 'month' : 'months'}
            </option>
          ))}
        </select>
      </label>
      <div className="flex gap-2 mt-5 justify-end">
        <button type="button" onClick={onDone} className="btn-ghost text-xs">
          Cancel
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={!phrase.trim()}
          className="btn-primary text-xs"
          data-testid="future-save"
        >
          <CalendarIcon size={12} /> Schedule
        </button>
      </div>
    </>
  );
}

function HandoffModal({ customer, onDone }: { customer: CustomerRecord; onDone: () => void }) {
  const artists = useBrainStore((s) => s.artists);
  const appendActivity = useBrainStore((s) => s.appendActivity);
  const pushNotification = useUIStore((s) => s.pushNotification);
  const clock = useSimulationStore((s) => s.clockIso);
  const candidates = Object.values(artists).filter((a) => !a.isApprentice).slice(0, 8);

  const onPick = (artistId: string) => {
    const artist = artists[artistId];
    if (!artist) return;
    appendActivity({
      id: `act-handoff-${Date.now()}`,
      type: 'ai-handoff-resolved',
      customerId: customer.id,
      artistId,
      body: `${customerName(customer)} routed to ${artist.displayName}. Brief sent to artist dashboard.`,
      timestamp: clock,
    });
    pushNotification({
      intensity: 'high',
      title: 'Hand-off complete',
      body: `${artist.displayName} will see ${customerName(customer)} in their queue.`,
      ttlMs: 4500,
    });
    onDone();
  };

  return (
    <>
      <p className="label-small-caps text-gold-bright">
        <Sparkles size={12} className="inline mr-2" /> Hand off to artist
      </p>
      <h2 className="heading-display text-2xl text-cream mt-2">{customerName(customer)}</h2>
      <p className="font-serif text-cream-soft text-sm mt-2">
        Pick the artist who&apos;ll take this customer. Their dashboard surfaces the brief, design
        intent, and rapport notes.
      </p>
      <ol className="mt-4 grid grid-cols-2 gap-2">
        {candidates.map((a) => (
          <li key={a.id}>
            <button
              type="button"
              onClick={() => onPick(a.id)}
              data-testid={`handoff-pick-${a.id}`}
              className="w-full text-left panel-dark p-3 hover:border-gold-bright/60 transition"
            >
              <p className="font-inter text-sm text-cream">{a.displayName}</p>
              <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
                {a.styleSpeciality.slice(0, 2).join(', ')}
              </p>
            </button>
          </li>
        ))}
      </ol>
    </>
  );
}

function CancelModal({ customer, onDone }: { customer: CustomerRecord; onDone: () => void }) {
  const bookings = useBrainStore((s) => s.bookings);
  const deposits = useBrainStore((s) => s.deposits);
  const upsertBooking = useBrainStore((s) => s.upsertBooking);
  const upsertDeposit = useBrainStore((s) => s.upsertDeposit);
  const upsertCustomer = useBrainStore((s) => s.upsertCustomer);
  const appendActivity = useBrainStore((s) => s.appendActivity);
  const pushNotification = useUIStore((s) => s.pushNotification);
  const clock = useSimulationStore((s) => s.clockIso);

  const upcoming = customer.bookings
    .map((id) => bookings[id])
    .filter((b): b is NonNullable<typeof b> => Boolean(b) && b!.status === 'confirmed');

  const [pickedId, setPickedId] = useState(upcoming[0]?.id ?? '');
  const [reason, setReason] = useState('Customer requested');
  const picked = pickedId ? bookings[pickedId] : undefined;

  const daysOut = picked
    ? Math.round((new Date(picked.start).getTime() - new Date(clock).getTime()) / 86_400_000)
    : 0;
  const rolls = daysOut > 7;

  const onConfirm = () => {
    if (!picked) return;
    upsertBooking({ ...picked, status: 'cancelled' });
    if (picked.depositId) {
      const dep = deposits[picked.depositId];
      if (dep) {
        upsertDeposit({
          ...dep,
          status: rolls ? 'rolled-over' : 'refunded',
        });
      }
      if (rolls) {
        upsertCustomer({
          ...customer,
          creditOnFile: {
            balanceAud: customer.creditOnFile.balanceAud + (deposits[picked.depositId]?.amountAud ?? 0),
            expiryDate: new Date(new Date(clock).setMonth(new Date(clock).getMonth() + 12)).toISOString(),
            history: [
              ...customer.creditOnFile.history,
              {
                id: `cof-${Date.now()}`,
                type: 'added',
                amountAud: deposits[picked.depositId]?.amountAud ?? 0,
                bookingId: picked.id,
                note: 'Rolled over from cancellation >7 days out',
                timestamp: clock,
              },
            ],
          },
        });
      }
    }
    appendActivity({
      id: `act-cancel-${picked.id}`,
      type: 'cancellation',
      customerId: customer.id,
      studioId: picked.studioId,
      artistId: picked.artistId,
      body: `${customerName(customer)} cancelled ${simTimeFull(picked.start)}. ${
        rolls ? `Deposit rolled to credit.` : `Deposit forfeit (<7 days).`
      } Reason: ${reason}.`,
      timestamp: clock,
    });
    pushNotification({
      intensity: 'high',
      title: 'Booking cancelled',
      body: rolls ? 'Deposit rolled to credit on file.' : 'Deposit forfeit per 7-day rule.',
      ttlMs: 5000,
    });
    onDone();
  };

  return (
    <>
      <p className="label-small-caps text-blood">
        <XCircle size={12} className="inline mr-2" /> Cancel booking
      </p>
      <h2 className="heading-display text-2xl text-cream mt-2">{customerName(customer)}</h2>

      {upcoming.length === 0 ? (
        <p className="font-serif text-cream-soft mt-4">
          No confirmed bookings to cancel. Try firing the Sarah Stripe deposit event first.
        </p>
      ) : (
        <>
          <label className="block mt-4">
            <span className="label-small-caps">Pick the booking</span>
            <select
              value={pickedId}
              onChange={(e) => setPickedId(e.target.value)}
              className="mt-2 w-full bg-ink-black border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
            >
              {upcoming.map((b) => (
                <option key={b.id} value={b.id}>
                  {simTimeFull(b.start)} · {b.artistId}
                </option>
              ))}
            </select>
          </label>
          <label className="block mt-3">
            <span className="label-small-caps">Reason</span>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mt-2 w-full bg-ink-black border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream"
            >
              <option>Customer requested</option>
              <option>Artist unavailable</option>
              <option>No-show</option>
              <option>Illness</option>
              <option>Double booking</option>
            </select>
          </label>
          <p className="mt-4 text-xs font-serif text-cream-soft">
            {picked
              ? rolls
                ? `${daysOut} days out → deposit rolls to credit (12-month expiry).`
                : `${daysOut} days out → deposit forfeit per 7-day rule.`
              : null}
          </p>
        </>
      )}

      <div className="flex gap-2 mt-5 justify-end">
        <button type="button" onClick={onDone} className="btn-ghost text-xs">
          Back
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={!picked}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-blood/80 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-cream hover:bg-blood disabled:opacity-40"
          data-testid="cancel-confirm"
        >
          <XCircle size={12} /> Confirm cancellation
        </button>
      </div>
    </>
  );
}
